import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Download, Users, UserX, UserCheck, Send, Trash2, Eye, BarChart3, X, Mail, MailCheck, MailWarning, RefreshCw, BadgeCheck, ShieldOff, TrendingUp } from "lucide-react";

const ROLES = [
  { key: "all", label: "All" },
  { key: "worker", label: "Workers" },
  { key: "contractor", label: "Contractors" },
  { key: "admin", label: "Admins" },
];

export default function AdminUsers() {
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (role !== "all") params.role = role;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
      setSelected(new Set());
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }, [role, q]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics/stats");
      setStats(data);
    } catch { /* ignore — non-blocking */ }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelected(new Set(users.filter((u) => u.role !== "admin").map((u) => u.id)));
  const clearSelection = () => setSelected(new Set());

  const setActive = async (u, active) => {
    if (u.role === "admin") return toast.error("Cannot modify admin account");
    try {
      await api.patch(`/admin/users/${u.id}`, { active });
      toast.success(active ? "User activated" : "User deactivated");
      fetchUsers();
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteUser = async (u) => {
    if (u.role === "admin") return toast.error("Cannot delete admin account");
    if (!window.confirm(`Delete ${u.name || u.mobile || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleVerify = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/verify`, { is_verified: !u.is_verified });
      toast.success(u.is_verified ? "Verification removed" : "Recruiter verified ✓");
      fetchUsers();
    } catch (e) { toast.error(formatError(e)); }
  };

  const exportCSV = () => {
    const rows = [
      ["ID", "Name", "Email", "Mobile", "Role", "Company", "Location", "Skills", "Experience (yrs)", "Applications", "Jobs Posted", "Active", "Joined"],
      ...users.map((u) => [
        u.id, u.name || "", u.email || "", u.mobile || "", u.role || "",
        u.company_name || "", u.location || "",
        Array.isArray(u.skills) ? u.skills.join("|") : "",
        u.experience_years || 0,
        u.applications_count || 0, u.jobs_posted_count || 0,
        u.active === false ? "No" : "Yes",
        (u.created_at || "").slice(0, 10),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sarthi-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const counterStats = useMemo(() => {
    if (!stats) return null;
    return [
      { label: "Total Users", value: stats.users?.total ?? 0, testid: "stat-users-total" },
      { label: "Workers", value: stats.users?.workers ?? 0, testid: "stat-workers" },
      { label: "Contractors", value: stats.users?.contractors ?? 0, testid: "stat-contractors" },
      { label: "Visitors (all-time)", value: stats.pageviews_total ?? 0, testid: "stat-visitors-total" },
      { label: "Visitors (today)", value: stats.pageviews_today ?? 0, testid: "stat-visitors-today" },
      { label: "Apply Clicks", value: stats.events?.apply_now_click ?? 0, testid: "stat-apply-clicks" },
    ];
  }, [stats]);

  const maxDay = useMemo(() => {
    if (!stats?.pageviews_7d?.length) return 1;
    return Math.max(1, ...stats.pageviews_7d.map((d) => d.count));
  }, [stats]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="admin-users-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">User Dashboard</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">All registered candidates, recruiters &amp; visitor analytics.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="send-weekly-report-btn"
            onClick={async () => {
              if (!window.confirm(`Send the 7-day growth report email now?`)) return;
              try {
                const { data } = await api.post("/admin/mailer/weekly-growth");
                toast.success(`Weekly report queued · ${data.new_workers + data.new_contractors + data.new_hr} new users, ${data.new_jobs} jobs, ${data.new_apps} apps → ${data.to}`);
              } catch (e) { toast.error(formatError(e)); }
            }}
            className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm font-bold"
            title="Fire the weekly growth summary email now"
          >
            <TrendingUp className="h-4 w-4" /> Send weekly report
          </button>
          <button data-testid="export-csv-btn" onClick={exportCSV} className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm font-bold">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Visitor counter + stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="stats-cards">
        {(counterStats || []).map((s) => (
          <div key={s.label} data-testid={s.testid} className="sarthi-card p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">{s.label}</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-sarthi-text">{s.value.toLocaleString("en-IN")}</div>
          </div>
        ))}
      </div>

      <MailerStatusCard />

      {/* 7-day visitor chart */}
      {stats?.pageviews_7d && (
        <div className="sarthi-card mt-4 p-4" data-testid="visitor-chart">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-sarthi-muted">
            <BarChart3 className="h-4 w-4" /> Visitors — last 7 days
          </div>
          <div className="flex h-32 items-end gap-2">
            {stats.pageviews_7d.map((d) => (
              <div key={d.date} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t-md bg-sarthi-primary transition-all"
                  style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%` }}
                  title={`${d.count} views`}
                />
                <div className="text-[10px] font-semibold text-sarthi-muted">{d.date.slice(5)}</div>
                <div className="text-xs font-bold">{d.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {ROLES.map((r) => (
          <button
            key={r.key}
            data-testid={`admin-role-${r.key}`}
            onClick={() => setRole(r.key)}
            className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${role === r.key ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
          >
            {r.label}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sarthi-muted" />
          <input
            data-testid="user-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
            placeholder="Search name / email / mobile / company"
            className="w-full rounded-xl border-2 border-sarthi-border bg-white px-9 py-2 text-sm font-semibold focus:border-sarthi-primary focus:outline-none"
          />
        </div>
      </div>

      {/* Selection toolbar */}
      {selected.size > 0 && (
        <div className="sarthi-card mt-4 flex flex-wrap items-center justify-between gap-3 border-sarthi-primary p-3" data-testid="selection-toolbar">
          <div className="text-sm font-bold text-sarthi-text">{selected.size} selected</div>
          <div className="flex gap-2">
            <button data-testid="broadcast-btn" onClick={() => setBroadcastOpen(true)} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
              <Send className="h-4 w-4" /> Broadcast
            </button>
            <button onClick={clearSelection} className="sarthi-btn-outline px-3 py-2 text-sm font-bold">Clear</button>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="sarthi-card mt-4 overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50">
            <tr className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">
              <th className="px-3 py-3">
                <input
                  data-testid="select-all"
                  type="checkbox"
                  checked={users.length > 0 && selected.size === users.length}
                  onChange={(e) => (e.target.checked ? selectAll() : clearSelection())}
                />
              </th>
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Contact</th>
              <th className="px-3 py-3">Role</th>
              <th className="px-3 py-3">Location</th>
              <th className="px-3 py-3">Skills / Company</th>
              <th className="px-3 py-3">Activity</th>
              <th className="px-3 py-3">Joined</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-sarthi-border/60 hover:bg-sarthi-accent/20">
                <td className="px-3 py-3">
                  <input
                    data-testid={`select-${u.id}`}
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggleSelect(u.id)}
                    disabled={u.role === "admin"}
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="font-semibold">{u.name || "—"}</div>
                  <div className="text-xs text-sarthi-muted">{u.experience_years ? `${u.experience_years} yr exp` : ""}</div>
                </td>
                <td className="px-3 py-3 text-sm">
                  <div>{u.email || <span className="text-sarthi-muted">no email</span>}</div>
                  <div>{u.mobile || <span className="text-sarthi-muted">no mobile</span>}</div>
                </td>
                <td className="px-3 py-3 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${u.role === "contractor" ? "bg-blue-100 text-blue-800" : u.role === "admin" ? "bg-purple-100 text-purple-800" : "bg-orange-100 text-orange-800"}`}>{u.role}</span>
                </td>
                <td className="px-3 py-3 text-sm">{u.location || "—"}</td>
                <td className="px-3 py-3 text-sm">
                  {u.role === "contractor" ? (u.company_name || "—") : (u.skills || []).slice(0, 3).join(", ") + ((u.skills || []).length > 3 ? "…" : "")}
                </td>
                <td className="px-3 py-3 text-xs">
                  {u.role === "worker" && <div>{u.applications_count || 0} applications</div>}
                  {u.role === "contractor" && <div>{u.jobs_posted_count || 0} jobs</div>}
                </td>
                <td className="px-3 py-3 text-xs text-sarthi-muted">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-xs">
                  <div className="flex flex-col gap-1">
                    {u.active === false
                      ? <span className="rounded-full bg-red-100 px-2 py-0.5 font-bold text-red-800">Inactive</span>
                      : <span className="rounded-full bg-green-100 px-2 py-0.5 font-bold text-green-800">Active</span>}
                    {(u.role === "contractor" || u.role === "hr") && u.is_verified && (
                      <span data-testid={`verified-tag-${u.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700 ring-1 ring-emerald-200">
                        <BadgeCheck className="h-3 w-3" /> Verified
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`view-${u.id}`} onClick={() => setDetail(u)} title="View" className="rounded-lg p-1.5 hover:bg-sarthi-accent">
                      <Eye className="h-4 w-4" />
                    </button>
                    {u.role !== "admin" && (
                      u.active === false ? (
                        <button data-testid={`activate-${u.id}`} onClick={() => setActive(u, true)} title="Activate" className="rounded-lg p-1.5 text-green-700 hover:bg-green-50">
                          <UserCheck className="h-4 w-4" />
                        </button>
                      ) : (
                        <button data-testid={`deactivate-${u.id}`} onClick={() => setActive(u, false)} title="Deactivate" className="rounded-lg p-1.5 text-orange-700 hover:bg-orange-50">
                          <UserX className="h-4 w-4" />
                        </button>
                      )
                    )}
                    {u.role !== "admin" && (
                      <button data-testid={`delete-${u.id}`} onClick={() => deleteUser(u)} title="Delete" className="rounded-lg p-1.5 text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {(u.role === "contractor" || u.role === "hr") && (
                      u.is_verified ? (
                        <button data-testid={`unverify-${u.id}`} onClick={() => toggleVerify(u)} title="Remove verification" className="rounded-lg p-1.5 text-emerald-700 hover:bg-emerald-50">
                          <ShieldOff className="h-4 w-4" />
                        </button>
                      ) : (
                        <button data-testid={`verify-${u.id}`} onClick={() => toggleVerify(u)} title="Verify recruiter" className="rounded-lg p-1.5 text-emerald-700 hover:bg-emerald-50">
                          <BadgeCheck className="h-4 w-4" />
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={10} className="p-10 text-center text-sarthi-muted">
                <Users className="mx-auto mb-2 h-8 w-8 opacity-50" />
                No users found
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={10} className="p-6 text-center text-sarthi-muted">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {broadcastOpen && (
        <BroadcastModal
          count={selected.size}
          userIds={Array.from(selected)}
          onClose={() => setBroadcastOpen(false)}
          onSent={() => { setBroadcastOpen(false); clearSelection(); }}
        />
      )}

      {detail && <DetailDrawer user={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function BroadcastModal({ count, userIds, onClose, onSent }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (msg.trim().length < 3) return toast.error("Message too short");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { user_ids: userIds, message: msg.trim() });
      toast.success(`Broadcast queued to ${data.count} users`);
      onSent();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="broadcast-modal">
      <div className="sarthi-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">Broadcast to {count} users</h2>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-sarthi-accent"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-xs text-sarthi-muted">Will attempt WhatsApp first, then SMS fallback (Twilio) — respects per-user opt-ins.</p>
        <textarea
          data-testid="broadcast-message"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={5}
          maxLength={1000}
          placeholder="Type your announcement… (max 1000 chars)"
          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base focus:border-sarthi-primary focus:outline-none"
        />
        <div className="mt-1 text-right text-xs text-sarthi-muted">{msg.length}/1000</div>
        <button data-testid="broadcast-send" onClick={send} disabled={busy || msg.trim().length < 3} className="sarthi-btn-primary mt-3 inline-flex w-full items-center justify-center gap-2 py-3 text-white disabled:opacity-50">
          <Send className="h-4 w-4" /> {busy ? "Sending…" : `Send to ${count} users`}
        </button>
      </div>
    </div>
  );
}

function DetailDrawer({ user, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="user-detail-drawer">
      <div className="sarthi-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">{user.name || "—"}</h2>
          <button onClick={onClose} className="rounded-lg p-1 hover:bg-sarthi-accent"><X className="h-5 w-5" /></button>
        </div>
        <dl className="space-y-2 text-sm">
          <Row k="Role" v={user.role} />
          <Row k="Email" v={user.email} />
          <Row k="Mobile" v={user.mobile} />
          <Row k="Location" v={user.location} />
          {user.role === "worker" && <>
            <Row k="Skills" v={(user.skills || []).join(", ")} />
            <Row k="Experience" v={user.experience_years ? `${user.experience_years} yrs` : "—"} />
            <Row k="Applications" v={user.applications_count} />
          </>}
          {user.role === "contractor" && <>
            <Row k="Company" v={user.company_name} />
            <Row k="GST" v={user.gst} />
            <Row k="Jobs Posted" v={user.jobs_posted_count} />
          </>}
          <Row k="WhatsApp opt-in" v={user.whatsapp_optin === false ? "No" : "Yes"} />
          <Row k="SMS opt-in" v={user.sms_optin === false ? "No" : "Yes"} />
          <Row k="Joined" v={(user.created_at || "").slice(0, 10)} />
          <Row k="Status" v={user.active === false ? "Inactive" : "Active"} />
        </dl>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-sarthi-border/40 py-1">
      <dt className="text-xs font-bold uppercase tracking-wide text-sarthi-muted">{k}</dt>
      <dd className="text-right text-sm font-semibold text-sarthi-text">{v || "—"}</dd>
    </div>
  );
}

function MailerStatusCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mailer/status");
      setStatus(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!status || !status.configured) return null;
  const ok = status.can_send;
  const pending = (status.verified_senders || []).filter((s) => !s.verified);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mailer/resend-verification");
      if (data.already_verified) toast.success("Sender is already verified — try Test send.");
      else if (data.ok) toast.success(`Verification email resent to sender inbox (id ${data.sender_id}). Check the inbox and click the link.`);
      else toast.error(`Resend failed: status ${data.status}`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mailer/test-send");
      if (data.ok) toast.success(`Test email sent to ${data.to} ✓`);
      else toast.error(`Test failed: ${data.note}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div
      data-testid="mailer-status-card"
      className={`sarthi-card mt-4 border-l-4 p-4 ${ok ? "border-l-green-500" : "border-l-amber-500"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {ok ? <MailCheck className="mt-0.5 h-6 w-6 text-green-600" /> : <MailWarning className="mt-0.5 h-6 w-6 text-amber-600" />}
          <div>
            <div className="font-display text-lg font-extrabold text-sarthi-text">
              Transactional email · {ok ? "LIVE" : "Pending verification"}
            </div>
            <div className="mt-0.5 text-sm text-sarthi-muted">
              Provider: SendGrid · Sender: <code className="rounded bg-sarthi-accent px-1.5 py-0.5 text-xs">{status.resolved_from}</code>
            </div>
            {!ok && pending.length > 0 && (
              <div className="mt-2 max-w-2xl text-xs text-sarthi-muted">
                SendGrid is blocking sends because the sender <code>{pending[0].from_email}</code> hasn't been verified yet.
                Click <strong>Resend verification</strong> below, then open that inbox and click the SendGrid confirmation link.
                Once verified, emails start flowing automatically — no restart needed.
              </div>
            )}
            {ok && (
              <div className="mt-2 text-xs text-green-800">Sender is verified. All transactional emails (welcome / applications / stage updates / receipts / broadcasts / enquiries) are being delivered.</div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="mailer-refresh"
            onClick={load}
            className="sarthi-btn-outline inline-flex items-center gap-2 px-3 py-2 text-xs font-bold"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          {!ok && (
            <button
              data-testid="mailer-resend-verification"
              onClick={resend}
              disabled={busy}
              className="sarthi-btn-primary inline-flex items-center gap-2 px-3 py-2 text-xs text-white disabled:opacity-50"
            >
              <Mail className="h-4 w-4" /> Resend verification
            </button>
          )}
          <button
            data-testid="mailer-test-send"
            onClick={test}
            disabled={busy}
            className="sarthi-btn-outline inline-flex items-center gap-2 px-3 py-2 text-xs font-bold disabled:opacity-50"
          >
            <Send className="h-4 w-4" /> Test send
          </button>
        </div>
      </div>
    </div>
  );
}
